import {
  faSort,
  faSortUp,
  faSortDown,
  faEdit,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Auth } from "aws-amplify";
import { debounce } from "lodash";
import { useState, useRef, useCallback, useEffect } from "react";
import { useConfigurations } from "../../../contexts/configurations-context";
import { useEthosNotification } from "../../../contexts/ethos-notification-context";
import { useServiceCustomer } from "../../../contexts/service-customer-context";
import { useUser } from "../../../contexts/user-context";
import {
  getProgressStatusLabelFromConfiguration,
  renderPaginator,
} from "../../../utils/functions";
import { NavLink } from "react-router-dom";
import moment from "moment";

const ListPracticesFinanthia = () => {
  const LIMIT = 12;
  const [offset, setOffset] = useState<number>(0);

  const [role, setUserRole] = useState<string | null>(null);

  const { configurations } = useConfigurations();
  const { getServiceCustomers, getServiceCustomersCsv } = useServiceCustomer();
  const { handleError } = useEthosNotification();
  const { getUsers } = useUser();

  const [paginationElement, setPaginationElement] =
    useState<JSX.Element | null>(null);

  const [finanthiaDataRequest, setFinanthiaDataRequest] = useState([]);

  const [search, setSearch] = useState<string | null>(null);

  const [finanthiaStatus, setFinanthiaStatus] = useState<string | null>(null);
  const [userAgent, setUserAgent] = useState<string | null>(null);

  const [selectFinanthiaStatus, setSelectFinanthiaStatus] = useState<
    { value: string; label: string }[] | null
  >(null);

  const [selectUserAgent, setSelectUserAgent] = useState<
    { value: string; label: string }[] | null
  >(null);

  const finanthiaStatusRef = useRef<HTMLSelectElement>();
  const userAgentRef = useRef<HTMLSelectElement>();
  const searchRef = useRef<HTMLInputElement>();

  const [sortColumn, setSortColumn] = useState<{
    column: string | null;
    direction: number;
  }>({ column: null, direction: 0 });
  const [sortFilter, setSortFilter] = useState<string | null>(null);

  async function getUserRole() {
    try {
      const session = await Auth.currentSession();
      const accessToken = session.getAccessToken();
      const payload = accessToken.payload;

      const role =
        payload["cognito:groups"] && payload["cognito:groups"].length
          ? payload["cognito:groups"][0]
          : "CLIENTE";

      setUserRole(role);
    } catch (error) {
      console.error("Error fetching user session:", error);
      setUserRole("CLIENTE");
    }
  }

  const initFinanthia = useCallback(async () => {
    const { data, error } = await getServiceCustomers(
      "Finanthia",
      LIMIT,
      offset,
      sortFilter,
      search,
      null,
      finanthiaStatus,
      null,
      userAgent,
    );

    if (error) {
      handleError(error);
      return;
    }

    setFinanthiaDataRequest(data.data);
    renderPaginator(
      data.length,
      LIMIT,
      setOffset,
      setPaginationElement,
      offset,
    );
  }, [
    finanthiaStatus,
    getServiceCustomers,
    handleError,
    offset,
    search,
    sortFilter,
    userAgent,
  ]);

  const initUsers = useCallback(async () => {
    const { data, error } = await getUsers(null, null, null, null);
    if (error) {
      handleError(error);
      return;
    }

    const response = data.data
      .filter((u) => u.isActive === true)
      .sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`,
        ),
      )
      .map((item) => ({
        value: item._id,
        label: `${item.first_name} ${item.last_name} (${item.username})`,
      }));
    setSelectUserAgent(response);
  }, [getUsers, handleError]);

  const init = useCallback(() => {
    initFinanthia();
  }, [initFinanthia]);

  const resetFilter = () => {
    setSortFilter(null);
    setOffset(0);
    finanthiaStatusRef.current.value = null;
    setFinanthiaStatus(null);
    userAgentRef.current.value = null;
    setUserAgent(null);
    searchRef.current.value = null;
    setSearch(null);
  };

  const getRowColorStatus = (status: string): string => {
    switch (status) {
      case "KO":
        return "red";
      case "RECUPERATA":
      case "COMPLETATO":
      case "CHIUSO":
        return "green";
      case "IN_CORSO":
        return "orange";
      default:
        return "";
    }
  };

  const onSearchChange = debounce((value: string) => {
    setSearch(value);
  }, 500);

  const onChangeStatus = debounce((value: string) => {
    setFinanthiaStatus(value);
  }, 500);

  const sortingColumn = (columnName: string) => {
    if (!!sortColumn) {
      if (sortColumn.column === columnName) {
        setSortColumn({
          column: columnName,
          direction: sortColumn.direction === 1 ? -1 : 1,
        });
      } else {
        setSortColumn({ column: columnName, direction: 1 });
      }
    }
  };

  const renderSortArrow = (columnName: string) => {
    if (sortFilter === null) {
      return (
        <FontAwesomeIcon
          icon={faSort}
          style={{ opacity: ".50" }}
          className="ml-2"
        />
      );
    }
    if (!!sortColumn) {
      if (sortColumn.column === columnName) {
        return sortColumn.direction === 1 ? (
          <FontAwesomeIcon icon={faSortUp} className="ml-2" />
        ) : (
          <FontAwesomeIcon icon={faSortDown} className="ml-2" />
        );
      }
    }
  };

  const getCustomerFullname = (item): string => {
    const customer = item.customerData.find((c) => c._id === item.customerId);
    if (!!customer) {
      return `${customer.first_name} ${customer.last_name}`;
    }
    return "N.D.";
  };

  const handleGenerateCSV = async () => {
    const { data, error } = await getServiceCustomersCsv(
      "Finanthia",
      LIMIT,
      offset,
      sortFilter,
      search,
      null,
      finanthiaStatus,
      null,
      userAgent,
    );
    if (error) {
      handleError(error);
      return;
    }

    const buffer = new Uint8Array(data.buffer.Body.data);
    const filename = data.filename;

    const blob = new Blob([buffer], {});

    const url = window.URL.createObjectURL(blob);
    const element = document.createElement("a");

    element.href = url;
    element.download = filename;

    document.body.appendChild(element);

    element.click();
    element.remove();
  };

  const isLoaded = () => {
    return configurations && role;
  };

  useEffect(() => {
    init();
  }, [offset, init]);

  useEffect(() => {
    if (!!sortColumn.column) {
      setSortFilter(`${sortColumn.column}:${sortColumn.direction}`);
    }
  }, [sortColumn]);

  useEffect(() => {
    getUserRole();
    initUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (configurations) {
      const list = (
        configurations.typeServiceStatus.finanthia as {
          value: string;
          label: string;
        }[]
      ).map((item) => {
        return {
          ...item,
        };
      });
      setSelectFinanthiaStatus(list);
    }
  }, [configurations]);

  return (
    <div className="list-finanthia-page">
      {isLoaded() && (
        <div className="row">
          <div className="col-12 text-center">
            <h3>Lista Gestione Finanthia</h3>
          </div>
          <div className="bg-light">
            <div className="d-flex justify-content-end align-items-center py-3">
              <div className="flex-1">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handleGenerateCSV();
                  }}
                >
                  Esporta in CSV
                </button>
              </div>
            </div>

            <div className="row align-items-center">
              <div className="col-12 col-md-12 col-lg-3 my-2">
                <input
                  ref={searchRef}
                  className="form-control"
                  placeholder="Cerca un cliente"
                  onChange={(e) => {
                    onSearchChange(e.target.value);
                  }}
                />
              </div>
              <div className="col-12 col-md-12 col-lg-3 my-2">
                <select
                  title="status select"
                  ref={finanthiaStatusRef}
                  className="form-select"
                  placeholder="cerca per stato"
                  onChange={(e) => {
                    onChangeStatus(e.target.value);
                  }}
                >
                  <option value="null">Filtra per stato avanzamento</option>
                  {selectFinanthiaStatus !== null &&
                    selectFinanthiaStatus.map((status, index) => {
                      return (
                        <option
                          key={`${status.value}_${index}`}
                          value={status.value}
                        >
                          {status.label}
                        </option>
                      );
                    })}
                </select>
              </div>
              {role === "ADMIN" ? (
                <>
                  <div className="col-12 col-md-12 col-lg-3 my-2">
                    <select
                      title="user select"
                      ref={userAgentRef}
                      className="form-select"
                      placeholder="Cerca un utente"
                      onChange={(e) => {
                        setUserAgent(e.target.value);
                      }}
                    >
                      <option value="null">Filtra per utente</option>
                      {selectUserAgent !== null &&
                        selectUserAgent.map((agent, index) => {
                          return (
                            <option
                              key={`${agent.value}_${index}`}
                              value={agent.value}
                            >
                              {agent.label}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                </>
              ) : null}
              <div className="col-12 col-md-12 col-lg-3 my-2">
                <div className="btn-group w-100">
                  <button className="btn btn-primary">Cerca</button>

                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      resetFilter();
                    }}
                  >
                    Resetta Filtri
                  </button>
                </div>
              </div>
            </div>
          </div>

          <hr className="my-3" />

          <div className="col-12 mt-1">
            {finanthiaDataRequest && finanthiaDataRequest.length > 0 ? (
              <nav className="pagination-section">
                <ul className="pagination justify-content-center">
                  {paginationElement}
                </ul>
              </nav>
            ) : null}
          </div>

          {/* TABLE */}
          <table className="table table-striped">
            <thead className="thead-dark">
              <tr>
                <th style={{ width: "5%" }}></th>
                <th></th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("customerData.first_name");
                  }}
                >
                  Cliente {renderSortArrow("customerData.first_name")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("serviceStatus.status");
                  }}
                >
                  Stato Avanzamento {renderSortArrow("serviceStatus.status")}
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    sortingColumn("createdAt");
                  }}
                >
                  Data Creazione {renderSortArrow("createdAt")}
                </th>
              </tr>
            </thead>
            <tbody className="finanthia-list">
              {finanthiaDataRequest != null &&
                finanthiaDataRequest.map((finanthia, index) => {
                  const canEdit =
                    finanthia.status === "ACTIVE" &&
                    finanthia.serviceStatus != null;

                  return (
                    <tr
                      key={`${finanthia._id}_${index}`}
                      className={getRowColorStatus(
                        finanthia.serviceStatus !== null
                          ? finanthia.serviceStatus.status
                          : "N.D.",
                      )}
                    >
                      <td>
                        {canEdit ? (
                          <NavLink
                            to={`service-details/${finanthia._id}`}
                            className="text-white"
                          >
                            <button title="edit" className="btn btn-secondary">
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          </NavLink>
                        ) : (
                          <button
                            title="edit"
                            className="btn btn-secondary"
                            disabled
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                        )}
                      </td>
                      <td>
                        {finanthia.serviceStatus === null ||
                        finanthia.serviceStatus.expireAt === null ? (
                          ""
                        ) : moment(finanthia.serviceStatus.expireAt) >
                          moment() ? null : (
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ color: "#f00" }}
                          />
                        )}
                      </td>
                      <td className="text-uppercase">
                        <a href={"/customers/" + finanthia.customerId}>
                          <span style={{ color: "black" }}>
                            {getCustomerFullname(finanthia)}
                          </span>
                        </a>
                      </td>
                      <td className="text-uppercase">
                        {finanthia.serviceStatus !== null
                          ? getProgressStatusLabelFromConfiguration(
                              finanthia.serviceStatus.status,
                              configurations,
                              "default",
                            )
                          : "N.D."}
                      </td>
                      <td>
                        {moment(finanthia.createdAt).format("DD/MM/YYYY")}
                      </td>
                    </tr>
                  );
                })}

              {finanthiaDataRequest.length === 0 && (
                <tr>
                  <th colSpan={9} style={{ textAlign: "center" }}>
                    Non è stato trovato nessun servizio di finanthia
                  </th>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ListPracticesFinanthia;
